import type {CoreEntityType} from 'nutshell-core/types';

import type {EntityType} from 'nutshell-graphql-types';
import type {EditionStub as Edition} from 'nutshell-graphql-types';

import type {OutreachItem} from './send-marketing-outreach-modal';

/**
 * This helper function converts a core entity type (leads, contacts, or accounts) to the entity type
 * the backend expects (LEAD, CONTACT, or ACCOUNT).
 *
 * @param entityType
 * @returns {string}
 */
export function getGraphqlEntityTypeFromCoreEntityType(entityType: CoreEntityType): EntityType {
    switch (entityType) {
        case 'leads':
            return 'LEAD';
        case 'contacts':
            return 'CONTACT';
        default:
            return 'ACCOUNT';
    }
}

/**
 * Returns the entity type in human readable format for singular or plural form.
 *
 * @param entityType
 * @param isPlural
 * @returns {string}
 */
export function getHumanReadableEntityType(entityType: CoreEntityType, isPlural?: boolean): string {
    switch (entityType) {
        case 'leads':
            return isPlural ? 'leads' : 'lead';
        case 'accounts':
            return isPlural ? 'companies' : 'company';
        default:
            // Should never hit the default case but 'contacts' case can be returned as default fallback
            return isPlural ? 'people' : 'person';
    }
}

/**
 * Returns the type of the outreach item
 *
 * @param outreachItem
 * @returns {string|"BROADCAST"|"NEWSLETTER"|"DRIP_SEQUENCE"}
 */
export function getOutreachItemType(outreachItem: OutreachItem) {
    if (outreachItem.__typename === 'Edition') {
        return outreachItem.campaign.type;
    } else {
        return 'DRIP_SEQUENCE';
    }
}

/**
 * Returns the type of the outreach item in readable format
 *
 * @param outreachItem
 * @returns {string|string}
 */
export function getHumanReadableOutreachItemType(outreachItem: OutreachItem) {
    if (outreachItem.__typename === 'Edition') {
        return outreachItem.campaign.type === 'NEWSLETTER' ? 'Newsletter' : 'Broadcast';
    } else {
        return 'Drip sequence';
    }
}

/**
 * Given an edition, returns true if it can be sent
 *
 * @param edition
 * @returns {boolean}
 */
export function canSendEditionFromList(edition: Edition): boolean {
    return getSendEditionFromListDisabledReasons(edition).length === 0;
}

export function getSendEditionFromListDisabledReasons(edition: Edition): string[] {
    const errorMessages = [];

    if (edition.scheduledTime) {
        errorMessages.push('This message is already scheduled to be sent');
    }

    if (!edition.from || edition.from.status !== 'VERIFIED') {
        errorMessages.push('This message is missing a verified sender address');
    }

    if (!edition.emailTemplate || !edition.emailTemplate.subject) {
        errorMessages.push('This message is missing a subject');
    }

    if (!edition.hasStreetAddress) {
        errorMessages.push('This message is missing a required mailing address');
    }

    if (!edition.hasUnsubscribeLink) {
        errorMessages.push('This message is missing a required unsubscribe link');
    }

    if (edition.isABTesting) {
        errorMessages.push('A/B tested messages cannot be sent from a list');
    }

    return errorMessages;
}
